import { PRODUCT_OPTIONS } from '@/constants/product/productOptions';
import { moisturizerItems, cleanserItems, maskItems, sunCareItems, maskPackItems } from '@/constants/product/category';

export const getCategoryUrl = (productValue: string): string => {
  // Find the matching product option to get the full value
  const option = PRODUCT_OPTIONS.find((opt) => opt.value === productValue);
  if (!option) {
    return '/shop/';
  }

  // For parent categories - the URL format is /collections/{category}/
  if (option.value.indexOf('-') === -1) {
    return `/collections/${option.value}/`;
  }

  // For subcategories - we need to find the parent category URL and append the subcategory
  const allOptions = [...moisturizerItems, ...cleanserItems, ...maskItems, ...sunCareItems, ...maskPackItems].find(
    (item) => {
      const slug = item.url.split('/').filter(Boolean).pop() || '';
      return slug === option.value;
    },
  );

  return allOptions?.url || `/collections/${option.value}/`;
};

export const buildFilterQuery = (termIds: number[]): string => {
  if (termIds.length === 0) {
    return '';
  }
  return `?filters=product_tag[${termIds.join('+')}]`;
};

/**
 * Generate URL based on the selection state of the product finder dropdowns
 *
 * Implements the following rules:
 * 1. If all dropdowns are unchanged: /shop/
 * 2. If only first dropdown changed: [category URL from constants]
 * 3. If first unchanged but second/third/both changed: /shop/?filters=product_tag[termId(s)]
 * 4. If first and (second or third) changed: [category URL]?filters=product_tag[termId(s)]
 */
export const generateUrl = ({
  productType,
  skinType,
  skinConcern,
  getSkinTypeTermId,
  getSkinConcernTermId,
}: {
  productType: string;
  skinType: string;
  skinConcern: string;
  getSkinTypeTermId: (slug: string) => number | undefined;
  getSkinConcernTermId: (slug: string) => number | undefined;
}): string => {
  const productChanged = productType !== 'all';
  const skinTypeChanged = skinType !== 'all';
  const concernChanged = skinConcern !== 'all';

  // Case 1: No changes to any dropdown
  if (!productChanged && !skinTypeChanged && !concernChanged) {
    return '/shop/';
  }

  // Case 2: Only product category changed
  if (productChanged && !skinTypeChanged && !concernChanged) {
    return getCategoryUrl(productType);
  }

  // Case 3: Product unchanged, but skin type and/or concern changed
  if (!productChanged && (skinTypeChanged || concernChanged)) {
    const termIds: number[] = [];

    if (skinTypeChanged) {
      const skinTypeId = getSkinTypeTermId(skinType);
      if (skinTypeId) {
        termIds.push(skinTypeId);
      }
    }

    if (concernChanged) {
      const concernId = getSkinConcernTermId(skinConcern);
      if (concernId) {
        termIds.push(concernId);
      }
    }

    if (termIds.length === 0) {
      return '/shop/';
    }

    return `/shop/${buildFilterQuery(termIds)}`;
  }

  // Case 4: Product category changed AND (skin type or concern changed)
  if (productChanged && (skinTypeChanged || concernChanged)) {
    const baseUrl = getCategoryUrl(productType);
    const termIds: number[] = [];

    if (skinTypeChanged) {
      const skinTypeId = getSkinTypeTermId(skinType);
      if (skinTypeId) {
        termIds.push(skinTypeId);
      }
    }

    if (concernChanged) {
      const concernId = getSkinConcernTermId(skinConcern);
      if (concernId) {
        termIds.push(concernId);
      }
    }

    if (termIds.length === 0) {
      return baseUrl;
    }

    // Ensure we're not appending a query parameter to a URL that already ends with a slash
    const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    return `${cleanBaseUrl}${buildFilterQuery(termIds)}`;
  }

  return '/shop/';
};
